import { getCurrencyMetadata, normalizeRegistryCurrency } from "./currencyRegistry.js";
import { getIntlFormatLocale, normalizeRegistryLocale } from "./localeRegistry.js";
import { getMarketProfileForManifest } from "./marketProfiles.js";
import { DEFAULT_CURRENCY, normalizeDisplayCurrency } from "./currencyResolution.js";
import { DEFAULT_LOCALE, normalizeLocale } from "./localeResolution.js";

const DEFAULT_FX_RATES = {
  COP: 1,
  USD: 0.00025,
  EUR: 0.00023,
  MXN: 0.0045,
  BRL: 0.0012,
  // Placeholder fallback only — not a live rate; replaced by backend FX in a later milestone.
  RUB: 0.022,
};

function resolveFxRates(options, storageCurrency) {
  if (options.ratesAvailable === false) {
    return { [storageCurrency]: 1 };
  }
  return { ...DEFAULT_FX_RATES, ...(options.rates || {}) };
}

/**
 * @param {number|null|undefined} amount
 * @param {object} [options]
 * @param {string} [options.displayCurrency]
 * @param {string} [options.storageCurrency]
 * @param {string} [options.locale]
 * @param {Record<string, number>|null} [options.rates]
 * @param {boolean} [options.showApproximation]
 * @param {boolean} [options.ratesAvailable]
 * @param {boolean} [options.ratesStale]
 */
export function formatMoney(amount, options = {}) {
  const storageCurrency = normalizeRegistryCurrency(options.storageCurrency ?? DEFAULT_CURRENCY);
  const displayCurrency = normalizeDisplayCurrency(options.displayCurrency ?? storageCurrency);
  const locale = normalizeLocale(options.locale ?? DEFAULT_LOCALE);
  const ratesStale = options.ratesStale === true;
  const rates = resolveFxRates(options, storageCurrency);

  if (amount == null || Number.isNaN(Number(amount))) {
    return "";
  }

  const rawValue = Number(amount);

  if (displayCurrency === storageCurrency || !rates[displayCurrency]) {
    return formatNativeCurrency(rawValue, storageCurrency, locale);
  }

  const storageRate = rates[storageCurrency] ?? 1;
  const displayRate = rates[displayCurrency] ?? 0;
  const converted = storageCurrency === "COP" ? rawValue * displayRate : (rawValue / storageRate) * displayRate;
  // Approximation marker is opt-in only; display FX is treated as a normal conversion
  // while a more accurate rate source is introduced.
  const showApproximation =
    options.showApproximation === true && (ratesStale || displayCurrency !== storageCurrency);
  const prefix = showApproximation ? "≈ " : "";
  return `${prefix}${formatNativeCurrency(converted, displayCurrency, locale)}`;
}

/**
 * Convert a stored amount (default COP) to a rounded display-currency value for numeric inputs.
 */
export function convertStorageAmountToDisplay(amount, options = {}) {
  const storageCurrency = normalizeRegistryCurrency(options.storageCurrency ?? DEFAULT_CURRENCY);
  const displayCurrency = normalizeDisplayCurrency(options.displayCurrency ?? storageCurrency);
  const rates = resolveFxRates(options, storageCurrency);

  if (amount == null || Number.isNaN(Number(amount))) {
    return null;
  }

  const rawValue = Number(amount);
  if (displayCurrency === storageCurrency || !rates[displayCurrency]) {
    return Math.round(rawValue);
  }

  const displayRate = rates[displayCurrency] ?? 0;
  if (!displayRate) {
    return Math.round(rawValue);
  }

  return Math.round(rawValue * displayRate);
}

/**
 * Convert a display-currency input value back to stored COP (or storage currency).
 */
export function convertDisplayAmountToStorage(displayAmount, options = {}) {
  const storageCurrency = normalizeRegistryCurrency(options.storageCurrency ?? DEFAULT_CURRENCY);
  const displayCurrency = normalizeDisplayCurrency(options.displayCurrency ?? storageCurrency);
  const rates = resolveFxRates(options, storageCurrency);

  if (displayAmount == null || Number.isNaN(Number(displayAmount))) {
    return null;
  }

  const rawValue = Number(displayAmount);
  if (displayCurrency === storageCurrency || !rates[displayCurrency]) {
    return Math.round(rawValue);
  }

  const displayRate = rates[displayCurrency] ?? 0;
  if (!displayRate) {
    return null;
  }

  return Math.round(rawValue / displayRate);
}

function formatNativeCurrency(value, currency, locale) {
  const meta = getCurrencyMetadata(currency);
  const intlLocale = getIntlFormatLocale(locale) || meta.intlFormatLocale;
  const formatted = new Intl.NumberFormat(intlLocale, {
    maximumFractionDigits: meta.decimals,
    minimumFractionDigits: meta.decimals,
  }).format(value);

  if (currency === "EUR") {
    return `€${formatted}`;
  }
  if (currency === "USD") {
    const intlLocale = getIntlFormatLocale(locale) || "";
    return intlLocale.startsWith("en") ? `$${formatted}` : `US$${formatted}`;
  }
  if (currency === "MXN") {
    return `MX$${formatted}`;
  }
  if (currency === "BRL") {
    return `R$${formatted}`;
  }
  if (currency === "RUB") {
    return `₽${formatted}`;
  }
  if (currency === "COP") {
    return `COP $${formatted}`;
  }
  return `${currency} ${formatted}`;
}

/**
 * @param {Date|string|number|null|undefined} value
 * @param {string} [locale]
 * @param {Intl.DateTimeFormatOptions} [options]
 */
export function formatDate(value, locale = DEFAULT_LOCALE, options = {}) {
  if (value == null || value === "") {
    return "";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const normalizedLocale = getIntlFormatLocale(normalizeLocale(locale));
  return new Intl.DateTimeFormat(normalizedLocale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  }).format(date);
}

/**
 * @param {number|null|undefined} value
 * @param {string} [locale]
 * @param {Intl.NumberFormatOptions} [options]
 */
export function formatNumber(value, locale = DEFAULT_LOCALE, options = {}) {
  if (value == null || Number.isNaN(Number(value))) {
    return "";
  }
  const normalizedLocale = getIntlFormatLocale(normalizeLocale(locale));
  return new Intl.NumberFormat(normalizedLocale, options).format(Number(value));
}

/**
 * @param {number|null|undefined} valueKm
 * @param {string} [locale]
 * @param {object|null} [manifest]
 */
export function formatDistance(valueKm, locale = DEFAULT_LOCALE, manifest = null) {
  if (valueKm == null || Number.isNaN(Number(valueKm))) {
    return "";
  }
  const profile = manifest ? getMarketProfileForManifest(manifest) : null;
  const unit = profile?.distanceUnit || "km";
  return `${formatNumber(valueKm, locale)} ${unit}`;
}

/**
 * @param {number|null|undefined} valueM2
 * @param {string} [locale]
 * @param {object|null} [manifest]
 */
export function formatArea(valueM2, locale = DEFAULT_LOCALE, manifest = null) {
  if (valueM2 == null || Number.isNaN(Number(valueM2))) {
    return "";
  }
  const profile = manifest ? getMarketProfileForManifest(manifest) : null;
  const unit = profile?.areaUnit || "m2";
  return `${formatNumber(valueM2, locale)} ${unit}`;
}

/** @deprecated use formatMoney with storageCurrency — kept for backward compatibility */
export function formatMoneyFromCop(amountCop, options = {}) {
  return formatMoney(amountCop, { ...options, storageCurrency: "COP" });
}
